import { NgClass } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AuthProfileBilling,
  shouldShowScbBillingBanner,
} from 'app/core/auth/auth-profile.models';
import { AuthService } from 'app/core/auth/auth.service';
import { PermissionService } from 'app/core/permission/permission.service';
import { SYSTEM_PERMISSIONS } from 'app/core/permission/system-permissions';
import { CompanyDatePipe } from 'app/core/pipes/company-date.pipe';
import { ShortNumberPipe } from 'app/core/pipes/short-number.pipe';
import { merge } from 'rxjs';
import { ScbBillingSeverity } from '../scb.enums';
import { ScbMyBillingDialogComponent } from './scb-my-billing-dialog.component';

@Component({
  selector: 'app-scb-tenant-billing-banner',
  standalone: true,
  imports: [
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    CompanyDatePipe,
    ShortNumberPipe,
  ],
  template: `
    @if (billing(); as b) {
      <div
        class="scb-billing-banner print:hidden"
        [ngClass]="toneClass()"
        role="status"
        aria-live="polite">
        <div class="scb-billing-banner__inner">
          <div class="scb-billing-banner__icon" aria-hidden="true">
            <mat-icon [svgIcon]="icon()"></mat-icon>
          </div>
          <div class="min-w-0 flex-1">
            <div class="scb-billing-banner__title">{{ title() }}</div>
            <div class="scb-billing-banner__msg">
              {{ b.message || 'Payment required for your service contract.' }}
              @if (b.openInvoiceCount) {
                <span class="ms-1">
                  · {{ b.openInvoiceCount }} open
                  <span
                    class="cursor-help font-semibold"
                    [matTooltip]="'PKR ' + (b.openAmount ?? 0).toLocaleString()">
                    (PKR {{ (b.openAmount ?? 0) | shortNumber }})
                  </span>
                </span>
              }
              @if (b.graceEndsOn) {
                <span class="ms-1">· Grace ends {{ b.graceEndsOn | companyDate }}</span>
              }
            </div>
          </div>
          <button
            mat-flat-button
            type="button"
            class="scb-billing-banner__cta shrink-0"
            (click)="openPendingInvoices()">
            View invoices
          </button>
        </div>
      </div>
    }
  `,
  host: {
    '[class.scb-billing-banner-host--visible]': '!!billing()',
  },
  styles: [
    `
      /* Collapsed when there is no billing alert — avoids empty white gap under the module header. */
      :host {
        display: none;
        flex: 0 0 auto;
        box-sizing: border-box;
      }
      :host.scb-billing-banner-host--visible {
        display: block;
        position: sticky;
        top: 0;
        z-index: 48;
        padding: 10px 12px 12px;
      }
      @media (min-width: 768px) {
        :host.scb-billing-banner-host--visible {
          padding: 12px 16px 14px;
        }
      }
      .scb-billing-banner {
        border: 1px solid transparent;
        border-radius: 12px;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.06);
        overflow: hidden;
      }
      .scb-billing-banner__inner {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        max-width: 100%;
      }
      @media (min-width: 768px) {
        .scb-billing-banner__inner {
          padding: 16px 20px;
          gap: 16px;
        }
      }
      .scb-billing-banner__icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 12px;
        display: grid;
        place-items: center;
        background: rgba(255, 255, 255, 0.55);
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
      }
      .scb-billing-banner__icon mat-icon {
        width: 20px;
        height: 20px;
        font-size: 20px;
        color: inherit;
      }
      .scb-billing-banner__title {
        font-size: 13.5px;
        font-weight: 800;
        letter-spacing: -0.2px;
        line-height: 1.3;
      }
      .scb-billing-banner__msg {
        margin-top: 3px;
        font-size: 12px;
        line-height: 1.45;
        opacity: 0.92;
      }
      .scb-billing-banner__cta {
        background: rgba(255, 255, 255, 0.98) !important;
        color: inherit !important;
        font-weight: 700 !important;
        border-radius: 10px !important;
        padding: 0 16px !important;
        min-height: 36px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
      }
      .scb-billing-banner--due {
        background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
        border-color: #f59e0b;
        color: #92400e;
      }
      .scb-billing-banner--grace {
        background: linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%);
        border-color: #ea580c;
        color: #9a3412;
      }
      .scb-billing-banner--suspended {
        background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);
        border-color: #f87171;
        color: #991b1b;
      }
      :host-context(.dark) .scb-billing-banner {
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(0, 0, 0, 0.25);
      }
      :host-context(.dark) .scb-billing-banner--due {
        background: rgba(245, 158, 11, 0.22);
        border-color: rgba(245, 158, 11, 0.45);
        color: #fcd34d;
      }
      :host-context(.dark) .scb-billing-banner--grace {
        background: rgba(234, 88, 12, 0.24);
        border-color: rgba(234, 88, 12, 0.45);
        color: #fdba74;
      }
      :host-context(.dark) .scb-billing-banner--suspended {
        background: rgba(220, 38, 38, 0.28);
        border-color: rgba(248, 113, 113, 0.45);
        color: #fca5a5;
      }
      :host-context(.dark) .scb-billing-banner__icon {
        background: rgba(15, 23, 42, 0.35);
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
      }
      :host-context(.dark) .scb-billing-banner__cta {
        background: rgba(15, 23, 42, 0.85) !important;
      }
    `,
  ],
})
export class ScbTenantBillingBannerComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly billing = signal<AuthProfileBilling | null>(null);

  ngOnInit(): void {
    // Permission first (super admin always allowed), then billing severity / open invoices.
    merge(this.auth.billing$(), this.permissions.permissionKeys$())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshBanner());

    // Seed immediately (BehaviorSubject may already have profile from AuthGuard).
    this.refreshBanner();
    if (!this.auth.profile()) {
      // Layout can mount before profile finishes — force a check so billing arrives.
      this.auth
        .check()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.refreshBanner());
    }
  }

  /**
   * Layer 1: `can_view_billing_status_banner` (super admin bypasses).
   * Layer 2: existing overdue / grace / suspended billing rules.
   */
  private refreshBanner(): void {
    if (!this.permissions.hasUserPermission(SYSTEM_PERMISSIONS.viewBillingStatusBanner)) {
      this.billing.set(null);
      return;
    }
    const b = this.auth.billing();
    this.billing.set(shouldShowScbBillingBanner(b) ? (b as AuthProfileBilling) : null);
  }

  toneClass(): string {
    const s = this.billing()?.severity ?? 0;
    if (s >= ScbBillingSeverity.Suspended) return 'scb-billing-banner--suspended';
    if (s >= ScbBillingSeverity.Grace) return 'scb-billing-banner--grace';
    return 'scb-billing-banner--due';
  }

  icon(): string {
    const s = this.billing()?.severity ?? 0;
    if (s >= ScbBillingSeverity.Suspended) return 'heroicons_outline:lock-closed';
    if (s >= ScbBillingSeverity.Grace) return 'heroicons_outline:exclamation-triangle';
    return 'heroicons_outline:bell-alert';
  }

  title(): string {
    const s = this.billing()?.severity ?? 0;
    if (s >= ScbBillingSeverity.Suspended) return 'Account suspended';
    if (s >= ScbBillingSeverity.Grace) return 'Payment grace period';
    return 'Billing reminder';
  }

  openPendingInvoices(): void {
    this.dialog.open(ScbMyBillingDialogComponent, {
      width: 'min(840px, 96vw)',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'scb-my-billing-dialog-panel',
    });
  }
}
